// Edge calls for activity cards, reactions and the activity list (apps/edge/src/cards.ts), one
// function per route like friendsApi.ts. A card and its audience are parsed before they leave the
// device, so one the edge would refuse is a rejection here and never a request.
import {
  ActivityList,
  CardList,
  CardView,
  OkResponse,
  PutCardRequest,
  PutReactionRequest,
  type CardAudience,
  type CardContent,
  type CardPeriod,
  type ReactionKind,
} from "@proef/contract";
import { call } from "../edgeApi";

const id = encodeURIComponent;

/** Shares the card for one period; the same period again replaces what was shared. */
export async function putCard(
  period: CardPeriod,
  key: string,
  content: CardContent,
  audience: CardAudience,
): Promise<CardView> {
  const body = PutCardRequest.parse({ content, audience });
  return await call(CardView, `/v1/cards/${period}/${id(key)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Stops sharing a period's card, whoever it was shown to. */
export function deleteCard(period: CardPeriod, key: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/cards/${period}/${id(key)}`, { method: "DELETE" });
}

/** The caller's own cards (with who sees each), or another person's as far as they may be seen. */
export function listUserCards(userId: string): Promise<CardList> {
  return call(CardList, `/v1/users/${id(userId)}/cards`);
}

export function putReaction(cardId: string, kind: ReactionKind): Promise<CardView> {
  return call(CardView, `/v1/cards/${id(cardId)}/reaction`, {
    method: "PUT",
    body: JSON.stringify(PutReactionRequest.parse({ kind })),
  });
}

export function deleteReaction(cardId: string): Promise<CardView> {
  return call(CardView, `/v1/cards/${id(cardId)}/reaction`, { method: "DELETE" });
}

/** One page of what friends and groups did; pass the previous page's `nextCursor` for the next. */
export function getActivity(cursor?: string): Promise<ActivityList> {
  // `sessions=1` and `shared=1` say this bundle can read session lines and shared tastings; an
  // older one parses the list strictly, so it is never sent a kind it has not heard of.
  const flags = "?sessions=1&shared=1";
  const query = cursor === undefined ? flags : `${flags}&cursor=${id(cursor)}`;
  return call(ActivityList, `/v1/activity${query}`);
}
